import TelemetryDeck, {
  type TelemetryDeckOptions,
  type TelemetryDeckPayload,
} from "@telemetrydeck/sdk";
import { store } from "@/store";

export const PRODUCT_USAGE_SIGNAL_TYPE = "feature.used";

export const ProductUsageFeatureKey = {
  SharedSpaceCreated: "shared_space_created",
  DocumentUploaded: "document_uploaded",
  DocumentPresented: "document_presented",
  PromomatsSearchUsed: "promomats_search_used",
  AnalyticsDashboardOpened: "analytics_dashboard_opened",
  SharedSpaceOpened: "shared_space_opened",
  SharedSpaceDocumentViewed: "shared_space_document_viewed",
  SharedSpaceDownloadClicked: "shared_space_download_clicked",
  DocumentDownloaded: "document_downloaded",
  DocumentReplacedOrUpdated: "document_replaced_or_updated",
  ThumbnailReplaced: "thumbnail_replaced",
  FolderCreated: "folder_created",
  WorkspaceCreated: "workspace_created",
  UserInvited: "user_invited",
} as const;

export type ProductUsageFeatureKey =
  (typeof ProductUsageFeatureKey)[keyof typeof ProductUsageFeatureKey];

export type ProductUsageSurface = "web" | "ios" | "shared_space";
export type ProductUsageSource =
  | "legacy"
  | "projector"
  | "shared_space"
  | "upload_service";

export interface ProductUsageContext {
  surface?: ProductUsageSurface;
  source?: ProductUsageSource;
  uploadContext?: "new_document" | "document_revision";
  presentationContext?: "projector_session";
  sharedSpaceContext?: "legacy_modal";
  assetType?: "document" | "video" | "audio";
  thumbnailSource?: "upload" | "video_frame";
}

type ProductUsageClient = {
  clientUser: string;
  signal: (type: string, payload?: TelemetryDeckPayload) => Promise<unknown>;
};

let client: ProductUsageClient | null = null;

function envString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;

  const normalized = value.trim();
  if (
    normalized.length === 0 ||
    ["null", "undefined"].includes(normalized.toLowerCase())
  ) {
    return undefined;
  }

  return normalized;
}

function envBoolean(value: unknown): boolean | undefined {
  if (typeof value !== "string") return undefined;

  const normalized = value.trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(normalized)) return true;
  if (["0", "false", "no", "off"].includes(normalized)) return false;
  return undefined;
}

function isDisabled(): boolean {
  return envBoolean(import.meta.env.VITE_TELEMETRYDECK_DISABLED) === true;
}

function getAppId(): string | undefined {
  if (isDisabled()) return undefined;
  return envString(import.meta.env.VITE_TELEMETRYDECK_APP_ID);
}

function getSalt(): string | undefined {
  return envString(import.meta.env.VITE_TELEMETRYDECK_SALT);
}

function getTelemetryDeckOptions(): TelemetryDeckOptions | null {
  const appID = getAppId();
  const salt = getSalt();
  if (!appID || !salt) return null;

  const target = envString(import.meta.env.VITE_TELEMETRYDECK_TARGET);
  const sessionID =
    typeof sessionStorage === "undefined"
      ? undefined
      : envString(sessionStorage.getItem("runId"));
  const testMode =
    envBoolean(import.meta.env.VITE_TELEMETRYDECK_TEST_MODE) ??
    import.meta.env.DEV;

  return {
    appID,
    clientUser: getClientUser(),
    ...(target ? { target } : {}),
    salt,
    ...(sessionID ? { sessionID } : {}),
    testMode,
  };
}

function getClient(): ProductUsageClient | null {
  if (client) return client;

  const options = getTelemetryDeckOptions();
  if (!options) return null;

  try {
    client = new TelemetryDeck(options);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("Product usage analytics disabled", error);
    }
    return null;
  }

  return client;
}

function getClientUser(): string {
  const user = store.getters.user;
  if (user?.id) return `firebase-user:${user.id}`;
  return "anonymous";
}

function getRole(): "authenticated" | "anonymous" {
  const user = store.getters.user;
  return user?.postgresId || user?.id ? "authenticated" : "anonymous";
}

async function hashIdentifier(
  scope: "tenant",
  value: string | null | undefined,
): Promise<string | undefined> {
  const salt = getSalt();
  if (!value || !salt || !globalThis.crypto?.subtle) return undefined;

  const data = new TextEncoder().encode(`${scope}:${value}:${salt}`);
  const digest = await globalThis.crypto.subtle.digest("SHA-256", data);

  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export async function buildFeatureUsagePayload(
  featureKey: ProductUsageFeatureKey,
  context: ProductUsageContext = {},
): Promise<TelemetryDeckPayload> {
  const user = store.getters.user;
  const tenantIdHash = await hashIdentifier("tenant", user?.tenantId);
  const payload: TelemetryDeckPayload = {
    feature_key: featureKey,
    surface: context.surface ?? "web",
    platform: "web",
    app_version:
      envString(import.meta.env.VITE_APP_VERSION) ??
      envString(import.meta.env.VITE_GIT_SHA) ??
      "unknown",
    environment: import.meta.env.MODE,
    role: getRole(),
  };

  if (tenantIdHash) payload.tenant_id_hash = tenantIdHash;
  if (context.source) payload.source = context.source;
  if (context.uploadContext) payload.upload_context = context.uploadContext;
  if (context.presentationContext) {
    payload.presentation_context = context.presentationContext;
  }
  if (context.sharedSpaceContext) {
    payload.shared_space_context = context.sharedSpaceContext;
  }
  if (context.assetType) payload.asset_type = context.assetType;
  if (context.thumbnailSource) {
    payload.thumbnail_source = context.thumbnailSource;
  }

  return payload;
}

export async function sendFeatureUsage(
  featureKey: ProductUsageFeatureKey,
  context: ProductUsageContext = {},
): Promise<void> {
  const telemetryClient = getClient();
  if (!telemetryClient) return;

  telemetryClient.clientUser = getClientUser();
  const payload = await buildFeatureUsagePayload(featureKey, context);
  await telemetryClient.signal(PRODUCT_USAGE_SIGNAL_TYPE, payload);
}

export function trackFeatureUsage(
  featureKey: ProductUsageFeatureKey,
  context: ProductUsageContext = {},
): void {
  void sendFeatureUsage(featureKey, context).catch((error) => {
    if (import.meta.env.DEV) {
      console.warn("Product usage analytics signal failed", error);
    }
  });
}

export function resetProductUsageAnalyticsForTesting(): void {
  client = null;
}
